import { CheckboxWithToolTip } from 'components/WithTooltip';
import { BadgeWithTooltip } from 'components/WithTooltip/BadgeWithTooltip';
import type { BimWorkOrderPriceData, BimWorkOrderTotalItemData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import type { AnyObject } from 'types/AnyObject';
import { Button, Checkbox, IconExpand, IconWarning, Loader } from 'uikit';
import { checkObjectHasPathByKey } from 'utils';

import type { ColumnFunctionCallbackParams, ICellRendererParams, RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';

import { BimWorkOrderRowDetailsType, BimWorkOrderRowTypeEnum } from '../types';

import { BimWorkOrderPriceTableContextMenu } from './components/BimWorkOrderPriceTableContextMenu';

export interface IBimWorkOrderRowDetails<T> {
    rowType: BimWorkOrderRowTypeEnum;
    data: T;
    detailsType: BimWorkOrderRowDetailsType;
}

export const BimWorkOrderMenuRenderer =
    <T extends boolean>({
        selectRowAndOpenDetailsModal,
        checkRowHandler,
        checkedRows,
        isBottomTotalRow,
        makeRefreshTable,
        hideCheckBoxAndMenu,
        workOrderStatus,
        contractStatus,
        statusProject,
        orderRules,
        hasInProgress,
    }: {
        isBottomTotalRow: T;
        selectRowAndOpenDetailsModal: (
            rowData: IBimWorkOrderRowDetails<T extends true ? BimWorkOrderTotalItemData : BimWorkOrderPriceData>,
        ) => void;
        checkRowHandler: ({ id, data }: { id: number; data: BimWorkOrderPriceData | null }) => void;
        checkedRows: { [key: number]: BimWorkOrderPriceData | null };
        makeRefreshTable: () => void;
        hideCheckBoxAndMenu?: boolean;
        workOrderStatus?: WorkOrderStatusEnum;
        contractStatus?: ContractStatusEnum;
        statusProject?: ProjectStatusEnum;
        orderRules?: IUseAccess['orderBim'];
        hasInProgress?: boolean;
    }) =>
    // eslint-disable-next-line react/display-name
    ({ data }: ColumnFunctionCallbackParams<T extends true ? BimWorkOrderTotalItemData : BimWorkOrderPriceData>) => {
        if (!data) {
            return <Loader />;
        }

        const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
        const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

        const isWorkOrderStatusInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;
        const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
        const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;
        const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

        const isHasActKC2 = (data as BimWorkOrderPriceData).hasActKC2;

        const openDetails = (detailsType?: BimWorkOrderRowDetailsType) => {
            if (isBottomTotalRow) {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.TotalsString,
                    data,
                    detailsType: BimWorkOrderRowDetailsType.Details,
                });
            } else if (
                detailsType &&
                [BimWorkOrderRowDetailsType.Revision, BimWorkOrderRowDetailsType.Update].includes(detailsType)
            ) {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.RegularString,
                    detailsType,
                    data,
                });
            } else if (detailsType === BimWorkOrderRowDetailsType.Analytics) {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.RegularString,
                    data,
                    detailsType: BimWorkOrderRowDetailsType.Analytics,
                });
            } else {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.RegularString,
                    data,
                    detailsType: BimWorkOrderRowDetailsType.Details,
                });
            }
        };

        const openActs = () => {
            selectRowAndOpenDetailsModal?.({
                rowType: BimWorkOrderRowTypeEnum.RegularString,
                data,
                detailsType: BimWorkOrderRowDetailsType.ActsKc2,
            });
        };

        const handleCheckbox =
            (id: number) =>
            ({ checked }: { checked: boolean }) => {
                if (!isBottomTotalRow) {
                    if (checked) {
                        checkRowHandler({ id, data: data as BimWorkOrderPriceData });
                    } else {
                        checkRowHandler({ id, data: null });
                    }
                }
            };

        const hideExpander = hideCheckBoxAndMenu && (data as any).topPinnedRow;

        const isCheckboxWithToolTip =
            isWorkOrderStatusInProgress && !(data as BimWorkOrderPriceData).canRemovedToActKC2;
        const disabledCheckbox =
            isCheckboxWithToolTip ||
            isWorkOrderStatusDelayed ||
            isWorkOrderStatusCompleted ||
            isContractStatusDelayed ||
            isContractStatusCompleted ||
            !orderRules?.canEdit ||
            isProjectStatusDelayed;

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {!hideCheckBoxAndMenu &&
                    !isBottomTotalRow &&
                    (isCheckboxWithToolTip && isHasActKC2 ? (
                        <CheckboxWithToolTip
                            checkboxProps={{ checked: false, style: { padding: '4px' } }}
                            tooltipProps={{
                                mode: 'mouseover',
                                content: 'В составе данной версии сметы имеются Акты КС-2. Удаление недоступно',
                            }}
                            fakeDisabled
                        />
                    ) : (
                        <Checkbox
                            checked={!!checkedRows[(data as BimWorkOrderPriceData)?.id]}
                            onChange={handleCheckbox((data as BimWorkOrderPriceData)?.id)}
                            disabled={disabledCheckbox}
                            style={{ padding: '4px' }}
                        />
                    ))}

                {!hideCheckBoxAndMenu && !isBottomTotalRow && (
                    <BimWorkOrderPriceTableContextMenu
                        data={data as BimWorkOrderPriceData}
                        makeRefreshTable={makeRefreshTable}
                        openDetails={openDetails}
                        openActs={openActs}
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                        orderRules={orderRules}
                        statusProject={statusProject}
                        hasInProgress={hasInProgress}
                    />
                )}
                {!hideExpander && !(data as any).topPinnedRow && (
                    <Button onClick={openDetails as any} iconLeft={IconExpand} onlyIcon view="clear" size="xs" />
                )}
            </div>
        );
    };

export const cellWithDifferentFiled =
    (field: string, dotNotationField: string, leftSideGetter?: (data: any) => React.ReactNode | undefined) =>
    // eslint-disable-next-line react/display-name
    (p: ICellRendererParams) => {
        const { data, formatValue } = p;
        const LeftSide = leftSideGetter?.(data);

        if (data === undefined) {
            return LeftSide || '';
        }

        if (data?.[R.split('.', dotNotationField)[0]]?.hasErrors) {
            return (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <BadgeWithTooltip
                        messages={data?.[R.split('.', dotNotationField)[0]]?.errors}
                        size="l"
                        badgeSize="s"
                        label="Ошибка"
                        status="error"
                        iconLeft={IconWarning}
                    />
                </div>
            );
        }

        const pathValue = R.path(R.split('.', dotNotationField), data);
        const fieldValue = data[field];
        let value: string | undefined = '';

        if (checkObjectHasPathByKey(dotNotationField, data)) {
            if (typeof pathValue === 'number' || typeof pathValue === 'string') {
                value = formatValue?.(pathValue);
            }
        } else if (typeof fieldValue === 'number' || typeof fieldValue === 'string') {
            value = formatValue?.(fieldValue);
        }

        return LeftSide ? (
            <span>
                {LeftSide} {value}
            </span>
        ) : (
            value
        );
    };

export const getBimWorkOrderRowClass: RowClassRules = {
    groupTitleClass: ({ data }: { data: AnyObject }) =>
        data?.isGroupTitle || data?.value?.isGroupTitle || data?.planValue?.isGroupTitle,
    topPinnedRowClass: ({ data }: { data: AnyObject }) => data?.topPinnedRow,
    bottomPinnedRowClass: ({ node }) => node.rowPinned === 'bottom',
};
